import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getStudents, logTaughtConcepts, getTaughtConcepts } from '../../api';

// Taught Log — the teacher records which concepts he covered on a given day.
// The student's Daily Test is built from exactly this log, so a typo here means
// questions that never appear: invalid codes are listed back loudly.

const HARSH_ID = 'fc08f536-057d-46f4-9a90-7d774959d312';
const PHYSICS_SUBJECT_ID = 1;
const THIN_CONCEPT = 10;   // fewer than this many questions and a concept is worth flagging

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Codes may be pasted separated by spaces, commas, semicolons or newlines.
function parseCodes(raw) {
  return (raw || '')
    .split(/[\s,;]+/)
    .map(c => c.trim().toUpperCase())
    .filter(Boolean);
}

export default function TaughtLog() {
  const [studentId, setStudentId] = useState(HARSH_ID);
  const [manualId, setManualId] = useState(false);   // fall back to a free-text id field
  const [taughtOn, setTaughtOn] = useState(todayIso());
  const [raw, setRaw] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState('');

  const studentsQ = useQuery({
    queryKey: ['students'],
    queryFn: () => getStudents().then(r => r.data),
    retry: 0,
  });
  const students = studentsQ.data || [];

  // Default the picker to Harsh if he is in the list, otherwise the first student.
  useEffect(() => {
    if (!students.length) return;
    const has = students.some(s => String(s.id) === String(studentId));
    if (!has) setStudentId(String(students[0].id));
  }, [students, studentId]);

  // If the student list cannot be fetched at all, drop to the editable id field.
  useEffect(() => { if (studentsQ.isError) setManualId(true); }, [studentsQ.isError]);

  const loadLog = useCallback(() => {
    if (!studentId) return;
    setLogLoading(true); setLogError('');
    getTaughtConcepts({ student_id: studentId, subject_id: PHYSICS_SUBJECT_ID, limit: 30 })
      .then(r => setLog(Array.isArray(r.data) ? r.data : []))
      .catch(e => setLogError(e.response?.data?.detail || 'Could not load the recent log. Press Refresh to try again.'))
      .finally(() => setLogLoading(false));
  }, [studentId]);

  useEffect(() => { loadLog(); }, [loadLog]);

  const codes = parseCodes(raw);

  const submit = async (e) => {
    e && e.preventDefault();
    setError(''); setResult(null);
    if (!studentId.trim()) { setError('Choose a student (or paste a student id) first.'); return; }
    if (!taughtOn) { setError('Pick the date the concepts were taught.'); return; }
    if (codes.length === 0) { setError('Paste at least one concept code, e.g. GRAV-01 GRAV-02.'); return; }
    setSaving(true);
    try {
      const r = await logTaughtConcepts({
        student_id: studentId.trim(),
        subject_id: PHYSICS_SUBJECT_ID,
        taught_on: taughtOn,
        codes,
      });
      setResult(r.data);
      setRaw('');
      loadLog();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save the log. Check your connection and press Save again.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 26 }}>Taught Log</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
          Record which concepts you taught on a day. The student's Daily Test is built from this
          log — until a concept is logged here, no question from it can appear.
        </p>
      </div>

      <form className="card" onSubmit={submit} style={{ marginBottom: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="form-group">
            <label className="form-label">Student</label>
            {manualId ? (
              <>
                <input className="form-input" value={studentId} onChange={e => setStudentId(e.target.value)}
                  placeholder="student id (uuid)" />
                {students.length > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--primary)', cursor: 'pointer' }}
                    onClick={() => setManualId(false)}>Choose from the list instead</span>
                )}
                {studentsQ.isError && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    The student list could not be loaded, so paste the id here.
                  </span>
                )}
              </>
            ) : studentsQ.isLoading ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading students…</div>
            ) : (
              <>
                <select className="form-select" value={studentId} onChange={e => setStudentId(e.target.value)}>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>{s.full_name || s.username}</option>
                  ))}
                </select>
                <span style={{ fontSize: 12, color: 'var(--primary)', cursor: 'pointer' }}
                  onClick={() => setManualId(true)}>Type a student id instead</span>
              </>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Date taught</label>
            <input className="form-input" type="date" value={taughtOn} onChange={e => setTaughtOn(e.target.value)} />
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 12 }}>
          <label className="form-label">Concept codes — separate with spaces, commas or new lines</label>
          <textarea className="form-textarea" rows={5} value={raw} onChange={e => setRaw(e.target.value)}
            placeholder="GRAV-01 GRAV-02 NLM-04"
            style={{ fontFamily: 'monospace' }} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {codes.length === 0 ? 'Physics · nothing typed yet' : `Physics · ${codes.length} code${codes.length === 1 ? '' : 's'}: ${codes.join(', ')}`}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save to the taught log'}
          </button>
          <button type="button" className="btn btn-ghost" disabled={saving}
            onClick={() => { setRaw(''); setResult(null); setError(''); }}>Clear the box</button>
        </div>

        {error && (
          <div style={{ marginTop: 14, background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
            {error}
          </div>
        )}

        {result && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="badge badge-green">{result.added} added</span>
              <span className="badge badge-blue">{result.skipped} already logged</span>
              {(result.invalid || []).length > 0
                ? <span className="badge badge-red">{result.invalid.length} not recognised</span>
                : <span className="badge badge-green">no typos</span>}
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {result.taught_on} · {result.total_concepts_to_date} concepts taught in total
              </span>
            </div>
            {(result.invalid || []).length > 0 && (
              <div style={{ marginTop: 12, background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ color: '#991B1B', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                  These codes were not recognised and were NOT logged:
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {result.invalid.map(c => (
                    <span key={c} style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, background: 'white', border: '1px solid #FCA5A5', color: '#991B1B', padding: '4px 10px', borderRadius: 6 }}>{c}</span>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: '#991B1B', marginTop: 8 }}>
                  Check the spelling against the concept list and paste the corrected codes above.
                </div>
              </div>
            )}
          </div>
        )}
      </form>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h2 style={{ fontSize: 18 }}>Recent log</h2>
          <button className="btn btn-ghost btn-sm" onClick={loadLog} disabled={logLoading}>
            {logLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
          Concepts with fewer than {THIN_CONCEPT} questions are flagged — the paper cannot draw much from those.
        </p>

        {logError && (
          <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
            {logError}
          </div>
        )}

        {logLoading ? <div className="spinner"></div>
          : log.length === 0 ? <div className="empty-state" style={{ padding: '28px 0' }}>Nothing logged for this student yet.</div>
            : log.map(day => (
              <div key={day.taught_on} style={{ paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ fontWeight: 600 }}>
                    {new Date(day.taught_on).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {(day.codes || []).length} concept{(day.codes || []).length === 1 ? '' : 's'}
                  </span>
                </div>
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>Code</th>
                      <th>Concept</th>
                      <th style={{ textAlign: 'right', width: 150 }}>Questions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(day.codes || []).map(c => {
                      const thin = (c.question_count ?? 0) < THIN_CONCEPT;
                      return (
                        <tr key={c.code}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.code}</td>
                          <td>{c.name}</td>
                          <td style={{ textAlign: 'right' }}>
                            {thin
                              ? <span className="badge badge-red">{c.question_count ?? 0} — too few</span>
                              : <span style={{ color: 'var(--text-muted)' }}>{c.question_count}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
      </div>
    </div>
  );
}
